/**
 * CPAYMENT-1822: the pick step — everything between «прикрепить чек» and a file existing.
 *
 * Until now the page started reporting only once a `change` event arrived carrying a file. When the
 * OS dialog gave nothing back — it did not open, or the client left it empty-handed — not a single
 * event was written, so the session was indistinguishable from a client who never touched the
 * button. Measured on prod (deposit 2142369, MiuiBrowser/Android): ~60 taps over six minutes,
 * `media_object` empty, zero `receipt.*` events, and the only surviving trace was in a session
 * replay.
 *
 * The four outcomes of one press are now distinct:
 *
 *   picker_opened → file_selected / file_rejected / file_unreadable   the pick worked
 *   picker_opened → pick_cancelled                                    dialog was up, left empty
 *   picker_opened → pick_empty                                        `change` with no file
 *   picker_opened → picker_silent                                     nothing came back at all
 *
 * `picker_silent` is not by itself a verdict of "the dialog never opened" — see the flags on the
 * event. What it does guarantee is that the case is now counted instead of invisible.
 */
import { track } from '../analytics/tracker';
import { beacon } from '../analytics/beacon';
import { AnalyticsBasePayload, AnalyticsEvent } from '../analytics/events';

/**
 * How long to wait for the dialog to produce anything. Deliberately generous: the client browses
 * his own storage in there, and a false `picker_silent` costs more than a late one — it is the
 * event we will build the "could not attach" count on.
 */
export const PICK_SILENCE_MS = 20000;

/** Fruitless presses before the page offers the client a way out. */
export const PICK_HELP_AFTER = 2;

export interface PickWatchOptions {
  /** Base payload at the moment of the event, or null while the transaction is not loaded yet. */
  getContext: () => AnalyticsBasePayload | null;
  /** Fruitless presses so far in this visit; 0 means a file was taken and the count reset. */
  onFruitless: (count: number) => void;
}

export interface PickWatch {
  /** Called immediately before `input.click()`. */
  opened: () => void;
  /** Native `cancel` on the input. */
  cancelled: () => void;
  /** `change` fired with an empty file list. */
  empty: () => void;
  /** A file arrived — stop watching and forget the fruitless presses. */
  picked: () => void;
  /** Screen unmounted: drop the timer and the listener. */
  dispose: () => void;
}

/**
 * `oncancel` is a `GlobalEventHandlers` property, so its presence does not prove that *this* input
 * will ever fire `cancel` — a browser can carry it for `<dialog>` alone. Reported as a hint next to
 * `cancelSeen`, which is the hard evidence, and never used to decide anything.
 */
const cancelSupported = (): boolean => {
  try {
    return typeof HTMLInputElement !== 'undefined' && 'oncancel' in HTMLInputElement.prototype;
  } catch {
    return false;
  }
};

export const createPickWatch = ({ getContext, onFruitless }: PickWatchOptions): PickWatch => {
  let attempt = 0;
  let fruitless = 0;
  let openedAt = 0;
  let timer = 0;
  let watching = false;
  // Counted once per press: a `cancel` that arrives after the watchdog already fired is still worth
  // recording, but it is the same fruitless press, not a second one.
  let counted = false;
  let wentHidden = false;
  let cancelSeen = false;

  const onVisibility = () => {
    if (document.visibilityState === 'hidden') wentHidden = true;
  };

  const stopTimer = () => {
    if (timer) {
      window.clearTimeout(timer);
      timer = 0;
    }
    if (watching) {
      document.removeEventListener('visibilitychange', onVisibility);
      watching = false;
    }
  };

  const report = (name: 'receipt.picker_opened' | 'receipt.pick_cancelled' | 'receipt.pick_empty') => {
    const context = getContext();
    if (!context) return;
    const payload = { ...context, attempt, waitedMs: openedAt ? Date.now() - openedAt : 0 };
    try {
      // The three share one payload shape, but a union-typed `name` does not narrow on its own.
      track({ name, payload } as AnalyticsEvent);
    } catch {
      // analytics must never break UX
    }
  };

  const countFruitless = () => {
    if (counted) return;
    counted = true;
    fruitless += 1;
    onFruitless(fruitless);
  };

  const onSilence = () => {
    timer = 0;
    stopTimer();
    const context = getContext();
    if (context) {
      try {
        track({
          name: 'receipt.picker_silent',
          payload: {
            ...context,
            attempt,
            waitedMs: Date.now() - openedAt,
            wentHidden,
            cancelSeen,
            cancelSupported: cancelSupported(),
          },
        });
      } catch {
        // analytics must never break UX
      }
    }
    // The one channel that survives a blocked Sentry tunnel, and the only way this outcome shows up
    // in Loki at all.
    beacon('receipt-pick-silent');
    countFruitless();
  };

  return {
    opened: () => {
      stopTimer();
      attempt += 1;
      counted = false;
      openedAt = Date.now();
      wentHidden = false;
      report('receipt.picker_opened');
      try {
        document.addEventListener('visibilitychange', onVisibility);
        watching = true;
        timer = window.setTimeout(onSilence, PICK_SILENCE_MS);
      } catch {
        // a browser that cannot arm the watchdog still gets the plain events above
      }
    },
    cancelled: () => {
      stopTimer();
      cancelSeen = true;
      report('receipt.pick_cancelled');
      countFruitless();
    },
    empty: () => {
      stopTimer();
      report('receipt.pick_empty');
      countFruitless();
    },
    picked: () => {
      stopTimer();
      counted = true;
      if (fruitless === 0) return;
      fruitless = 0;
      onFruitless(0);
    },
    dispose: stopTimer,
  };
};
